import React from 'react';
import { Card } from './ui/card';
import { MapPin, Phone, Mail, MessageCircle } from 'lucide-react';

export const LocationsMap = ({ locations, openingHours }) => {
  return (
    <section id="kontakt" className="py-24 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
            Finden Sie uns
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Besuchen Sie uns an einem unserer drei Standorte in Magdeburg
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 mb-12">
          {locations.map((location, index) => (
            <Card
              key={location.id}
              className="overflow-hidden rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-500 border-0"
            >
              {/* Brand Logo */}
              <div className="bg-white p-6 flex justify-center border-b">
                <img
                  src={location.logo}
                  alt={`${location.brand} logo`}
                  className="h-16 w-auto object-contain"
                />
              </div>

              {/* Map */}
              <div className="relative h-48 overflow-hidden">
                <iframe
                  src={location.mapUrl}
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title={`${location.brand} Standort ${location.address.street}`}
                ></iframe>
              </div>

              {/* Content */}
              <div className="p-6 bg-white">
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  Autohaus Wilke - {location.brand}
                </h3>
                {location.additionalBrandsNote && (
                  <div className="inline-flex items-center gap-1.5 mb-4 px-3 py-1 bg-amber-50 border border-amber-200 rounded-full text-amber-800 text-xs font-medium" data-testid={`location-note-${location.id}`}>
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-500"></span>
                    {location.additionalBrandsNote}
                  </div>
                )}
                {!location.additionalBrandsNote && <div className="mb-4"></div>}
                
                <div className="space-y-3 mb-6">
                  {/* Address */}
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-red-50 to-red-100 rounded-lg flex items-center justify-center">
                      <MapPin className="w-5 h-5 text-red-600" />
                    </div>
                    <div>
                      <p className="text-gray-900 font-semibold text-sm">
                        {location.address.street}
                      </p>
                      <p className="text-gray-600 text-sm">
                        {location.address.zip} {location.address.city}
                      </p>
                    </div>
                  </div>

                  {/* Phone */}
                  <div className="flex items-center gap-3">
                    <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-red-50 to-red-100 rounded-lg flex items-center justify-center">
                      <Phone className="w-5 h-5 text-red-600" />
                    </div>
                    <a
                      href={`tel:${location.phone.replace(/\s/g, '')}`}
                      className="text-gray-900 font-semibold text-sm hover:text-red-600 transition-colors"
                    >
                      {location.phone}
                    </a>
                  </div>

                  {/* Email */}
                  <div className="flex items-center gap-3">
                    <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-red-50 to-red-100 rounded-lg flex items-center justify-center">
                      <Mail className="w-5 h-5 text-red-600" />
                    </div>
                    <a
                      href={`mailto:${location.email}`}
                      className="text-gray-900 font-semibold text-sm hover:text-red-600 transition-colors"
                    >
                      {location.email}
                    </a>
                  </div>

                  {/* WhatsApp */}
                  {location.whatsapp && (
                    <div className="flex items-center gap-3">
                      <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-green-50 to-green-100 rounded-lg flex items-center justify-center">
                        <MessageCircle className="w-5 h-5 text-green-600" />
                      </div>
                      <a
                        href={`https://wa.me/${location.whatsapp.replace(/[^0-9]/g, '')}?text=Hallo,%20ich%20interessiere%20mich%20für%20ein%20Fahrzeug%20am%20Standort%20${encodeURIComponent(location.address.street)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-gray-900 font-semibold text-sm hover:text-green-600 transition-colors"
                        data-testid={`location-whatsapp-${location.id}`}
                      >
                        WhatsApp: {location.whatsappDisplay || location.whatsapp}
                      </a>
                    </div>
                  )}
                </div>

                {/* Accent Border */}
                <div
                  className="absolute top-0 left-0 right-0 h-1"
                  style={{ backgroundColor: location.color }}
                ></div>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};
